package one.healthium.today.ui

import co.touchlab.kermit.Logger
import kotlinx.coroutines.NonCancellable.isActive
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import one.healthium.base.preferences.AppPreferences
import one.healthium.base.utils.toInstant
import one.healthium.domain.user.UserInteractor
import one.healthium.domain.user.UserModel
import one.healthium.domain.user.models.FoodLogModel
import one.healthium.healthKit.data.HealthKitHelper
import one.healthium.healthKit.ui.HealthKitConnectBannerContract
import one.healthium.insights.domain.content.ContentOptions
import one.healthium.insights.domain.content.ContentRepository
import one.healthium.insights.domain.models.ContentScreenModel
import one.healthium.meal_plans.domain.MealPlanInfoModel
import one.healthium.meal_plans.domain.MealPlanRepository
import one.healthium.tasks.domain.DailyTaskModel
import one.healthium.tasks.domain.DailyTasksRepository
import one.healthium.today.ui.calories_counter.CaloriesCounterUiModel
import one.healthium.today.ui.calories_counter.NutritionGoalUiModel
import one.healthium.today.ui.models.DailyStepsUiModel
import one.healthium.ui.common.event_producer.EventProducer
import one.healthium.ui.common.message.UiMessageManager
import one.healthium.ui.common.vm.BaseViewModel
import one.primehealth.app.BaseAppConfig
import kotlin.time.Duration
import kotlin.time.Duration.Companion.minutes

class TodayViewModel(
    userInteractor: UserInteractor,
    appPreferences: AppPreferences,
    mealPlanRepository: MealPlanRepository,
    private val healthKitHelper: HealthKitHelper,
    dailyTasksRepository: DailyTasksRepository,
    contentRepository: ContentRepository,
    baseAppConfig: BaseAppConfig,
) : BaseViewModel(), HealthKitConnectBannerContract {

    private val _state = MutableStateFlow(TodayUiState(isMetric = appPreferences.isMetric))

    fun state(contentOptions: ContentOptions? = null): StateFlow<TodayUiState> {
        if (contentOptions != null && contentOptionsFlow.value != contentOptions) {
            contentOptionsFlow.value = contentOptions
        }
        return _state.asStateFlow()
    }

    val uiMessages = UiMessageManager()
    val events = EventProducer<TodayUiEvent>()

    private val contentOptionsFlow = MutableStateFlow<ContentOptions?>(null)

    init {
        combine(
            userInteractor.observeUser(),
            mealPlanRepository.getCurrentMealPlan().catch { emit(null) },
            healthKitHelper.healthKitStatus,
            healthKitHelper.getDailySteps(),
            dailyTasksRepository.getDailyTasks(),
            contentOptionsFlow.flatMapLatest { contentOptions ->
                if (contentOptions != null) {
                    contentRepository.getContent(contentOptions)
                } else {
                    flow<ContentScreenModel?> { emit(null) }
                }
            },
            appPreferences.latestReadInsightTimestampFlow,
            appPreferences.latestReadMealPlanTimestampFlow,
            appPreferences.latestLogFoodTimestampFlow,
            tickerFlow(1.minutes),
        ) { values ->
            val user = values[0] as UserModel
            val mealPlan = values[1] as? MealPlanInfoModel
            val dailySteps = values[3] as Long?
            val contentScreenModel = values[5] as? ContentScreenModel
            val latestReadInsightTimestamp = values[6] as? Long
            val latestReadMealPlanTimestamp = values[7] as? Long
            val latestLogFoodTimestamp = values[8] as? Long
            @Suppress("UNCHECKED_CAST") val dailyTasks = values[4] as List<DailyTaskModel>
            _state.update { state ->
                state.copy(
                    todayCaloriesCounter = user.todayFoodLog.toCaloriesCounterUiModel(user),
                    todayStepsCounter = user.toStepsUiModel(dailySteps),
                    mealPlan = mealPlan,
                    foodLogs = user.foodLogs,
                    userModel = user,
                    shouldShowHKBanner = appPreferences.didReactToHKBanner == null && baseAppConfig.features.healthKitEnabled(),
                    dailyTasks = dailyTasks,
                    content = contentScreenModel,
                    latestReadInsightDate = latestReadInsightTimestamp?.toInstant(),
                    latestReadMealPlanDate = latestReadMealPlanTimestamp?.toInstant(),
                    latestLogFoodDate = latestLogFoodTimestamp?.toInstant(),
                )
            }
        }
            .catch { e ->
                Logger.e("TodayViewModel error", e)
            }
            .launch()

        appPreferences.isMetricFlow
            .onEach { isMetric ->
                _state.update { state -> state.copy(isMetric = isMetric) }
            }
            .launch()

        appPreferences.periodTrackerEnabledFlow
            .onEach { periodTrackerEnabled ->
                _state.update { state -> state.copy(showPeriodTracker = periodTrackerEnabled) }
            }
            .launch()
    }

    override fun onSkipClicked() {
        healthKitHelper.processSkipRequest()
    }

    private fun FoodLogModel?.toCaloriesCounterUiModel(user: UserModel) = this?.let {
        CaloriesCounterUiModel(
            caloriesGoal = NutritionGoalUiModel(user.caloriesGoal, food.sumOf { it.calories.toDouble() }.toFloat()),
            fatGoal = NutritionGoalUiModel(user.fatGoal, food.sumOf { it.fat.toDouble() }.toFloat()),
            carbohydratesGoal = NutritionGoalUiModel(user.carbohydratesGoal, food.sumOf { it.carbohydrates.toDouble() }.toFloat()),
            fiberGoal = NutritionGoalUiModel(user.fiberGoal, food.sumOf { it.fiber.toDouble() }.toFloat()),
            proteinGoal = NutritionGoalUiModel(user.proteinGoal, food.sumOf { it.protein.toDouble() }.toFloat()),
        )
    }

    private fun UserModel.toStepsUiModel(dailySteps: Long?): DailyStepsUiModel? {
        dailySteps?.let { steps ->
            return DailyStepsUiModel(
                steps = steps,
                goal = expectedDailySteps,
            )
        }
        return null
    }
}

private fun tickerFlow(period: Duration): Flow<Unit> = flow {
    while (isActive) {
        emit(Unit)
        delay(period)
    }
}